import React, { useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useWindowSize } from 'hooks/useWindowSize'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentThread } from 'components/comments/CommentThread'
import { EditorTOC } from './EditorTOC'
import { PacketHeader } from './PacketHeader'
import { useActiveSection } from './useActiveSection'
import { useCommentAnchors } from './useCommentAnchors'
import { SECTIONS } from './sections'
import { dark, light, radius, type } from '../tokens'
import { PerformanceReviewSection } from './sections/PerformanceReviewSection'
import { NarrativeSection } from './sections/NarrativeSection'
import { AudienceFrameworkPlaceholderSection } from './sections/AudienceFrameworkPlaceholderSection'
import { CreativePlaceholderSection } from './sections/CreativePlaceholderSection'
import { FinalApprovalSection } from './sections/FinalApprovalSection'

const HEADER_OFFSET = 60

interface Props {
  businessID: number
  packetID: number
}

export function PacketEditorBody({ businessID, packetID }: Props) {
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const business = useGetBusinessIfExists()
  const [windowHeight] = useWindowSize()
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const ready = !isLoading && !isError && !!packet
  const activeKey = useActiveSection({ sections: SECTIONS, scrollRootRef, ready })
  const { anchor, open, close } = useCommentAnchors(scrollRootRef)
  // Dev-only: `?theme=dark` flips to the dark palette so v0.13 (PDF) and v0.14
  // (Present mode) can be visually validated through the real editor without
  // standing up Storybook. Defaults to `light` for AE-facing edit mode.
  const { search } = useLocation()
  const palette = new URLSearchParams(search).get('theme') === 'dark' ? dark : light

  const scrollHeight = windowHeight - HEADER_OFFSET

  return (
    <>
      <div
        ref={scrollRootRef}
        style={{
          height: scrollHeight,
          maxHeight: scrollHeight,
          background: palette.bg,
          color: palette.text,
          fontFamily: type.family.sans,
        }}
        className="w-full overflow-y-auto"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <CircularProgress size={28} />
          </div>
        ) : isError || !packet ? (
          <div style={{ padding: 24 }}>
            <div
              style={{
                background: palette.surface,
                border: `1px solid ${palette.hairline}`,
                borderRadius: radius.lg,
                padding: 40,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                textAlign: 'center',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: type.family.sans,
                  fontSize: type.size.h2,
                  fontWeight: type.weight.semibold,
                  color: palette.text,
                }}
              >
                Couldn't load this sprint packet
              </h2>
              <p style={{ fontSize: type.size.body, color: palette.textMuted, maxWidth: 320 }}>
                Something went wrong fetching the packet. Try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                style={{
                  padding: '8px 16px',
                  borderRadius: radius.sm,
                  border: `1px solid ${palette.hairline}`,
                  background: palette.surface,
                  color: palette.text,
                  fontFamily: type.family.mono,
                  fontSize: type.size.eyebrow,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="flex w-full min-h-full">
            <EditorTOC palette={palette} activeKey={activeKey} />
            <div className="flex-1 flex flex-col min-w-0">
              <PacketHeader
                palette={palette}
                businessName={business?.businessName}
                phase={business?.phase}
                sprint={packet.sprint}
              />
              <main className="px-6 py-4 space-y-6 w-full">
                {SECTIONS.map(s => {
                  switch (s.kind) {
                    case 'performance':
                      return (
                        <PerformanceReviewSection key={s.key} palette={palette} packet={packet} />
                      )
                    case 'narrative':
                      return (
                        <NarrativeSection
                          key={s.key}
                          palette={palette}
                          sectionKey={
                            s.key as Exclude<
                              typeof s.key,
                              | 'performance-review'
                              | 'audience-framework'
                              | 'creative'
                              | 'final-approval'
                            >
                          }
                          label={s.label}
                          packetID={packet.id}
                          narrative={packet.narrative}
                        />
                      )
                    case 'audience-placeholder':
                      return (
                        <AudienceFrameworkPlaceholderSection
                          key={s.key}
                          palette={palette}
                          packetID={packet.id}
                        />
                      )
                    case 'creative-placeholder':
                      return (
                        <CreativePlaceholderSection
                          key={s.key}
                          palette={palette}
                          packetID={packet.id}
                        />
                      )
                    case 'approval':
                      return <FinalApprovalSection key={s.key} palette={palette} packet={packet} />
                  }
                })}
                <div className="h-[60vh]" aria-hidden />
              </main>
            </div>
          </div>
        )}
      </div>
      {packet && (
        <CommentThread
          open={open}
          onClose={close}
          businessID={businessID}
          anchor={anchor}
          notificationContext={{
            header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
            pageUrlBase: window.location.href.split('?')[0],
          }}
        />
      )}
    </>
  )
}
